import React, { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../components";
import { useForm, useOnlineStatus, useFetchAndSyncData } from "../../hooks";
import { loginFormValidations } from "../../helpers/formValidations";
import {
  reqToUserLogin,
  reqToForceUserLogout,
  reqToUserLoginInLocal,
} from "../../store/slice/userSlice";
import {
  notify,
  manageErrorLogs,
  dateFormatWithTime,
} from "../../helpers/commonFunction";
import { getLatestDataFromDB } from "../../helpers/offlineAPIs";
import { OfflineModal, ConfirmationModal } from "../../components/Modals";
import { messages } from "../../helpers/messages";
import { clearTablesWithTran } from "../../helpers/db";
import { routes } from "../../helpers/constants";
import { useLocation, useNavigate } from "react-router-dom";
import LocalDBContext from "../../helpers/LocalDBContext";

const LoginPage = ({ userRole }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const { localDBList } = useContext(LocalDBContext);
  const { userData } = localDBList;
  const { loader } = useSelector((state) => state.userReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyLoginModal, setAlreadyLoginModal] = useState(false);
  const handleAlreadyLoginModal = () => {
    setAlreadyLoginModal(!alreadyLoginModal);
  };

  const initialFValues = {
    username: "",
    password: "",
    type: userRole || "LAB",
  };
  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    return loginFormValidations(
      fieldValues,
      temp,
      values,
      setValues,
      setErrors
    );
  };
  const { errors, setErrors, values, setValues, handleInputChange } = useForm(
    initialFValues,
    true,
    validate
  );

  useEffect(() => {
    if (userData && Object.keys(userData)?.length > 0) {
      setValues({
        ...values,
        username: userData?.username || "",
        password: userData?.password || "",
      });
    }
  }, [userData]);

  const {
    syncType,
    syncMessage,
    offlineModal,
    confirmationModal,
    handleOfflineModal,
    handleConfirmModal,
    uploadAndFetchData,
  } = useFetchAndSyncData();

  const handleOfflineOnlineLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    if (validate() && location.pathname === routes.login && !userRole) {
      try {
        if (isOnline && (!userData || Object.keys(userData)?.length === 0)) {
          await clearTablesWithTran("userData");
          dispatch(reqToForceUserLogout());
          handleUserLogin();
        } else if (isOnline && userData && Object.keys(userData)?.length > 0) {
          await uploadAndFetchData("upload").then(async (res) => {
            if (res) {
              await clearTablesWithTran("userData");
              dispatch(reqToForceUserLogout());
              handleUserLogin(userData);
            }
          });
        } else if (!isOnline && userData && Object.keys(userData)?.length > 0) {
          setIsLoading(false);
          if (values.username === userData.username) {
            dispatch(reqToUserLoginInLocal(userData));
            navigate(routes.dashboard);
          } else {
            handleOfflineModal();
          }
        } else if (
          !isOnline &&
          (!userData || Object.keys(userData)?.length === 0)
        ) {
          setIsLoading(false);
          handleOfflineModal();
        }
      } catch (error) {
        setIsLoading(false);
        manageErrorLogs(error);
        notify("Failed to login!", "error");
      }
    } else if (validate() && location.pathname !== routes.login) {
      await clearTablesWithTran("userData");
      dispatch(reqToForceUserLogout());
      handleUserLogin();
    } else {
      setIsLoading(false);
    }
  };
  const handleUserLogin = (userDetails) => {
    dispatch(
      reqToUserLogin({
        data: { ...values, date: dateFormatWithTime(new Date()) },
        onSuccess: async () => {
          if (!userRole) {
            getLatestDataFromDB("login", dispatch, userDetails);
            setIsLoading(false);
          }
        },
        onFailure: (err) => {
          setIsLoading(false);
          if (err && err.response.data.includes("currently signed")) {
            handleAlreadyLoginModal();
          }
        },
      })
    );
  };

  return (
    <>
      {loader && <Loader />}
      <div className="tc-login-section">
        <div className="tc-navbar"></div>
        <div className="tc-login-content">
          <Form className="tc-login-form">
            <div className="tc-login-top-space"></div>
            <div className="tc-login-form-box">
              <div
                className="tc-login-logo"
                style={{ padding: "30px 15px 15px 15px" }}
              >
                <div className="logo-title">
                  <h2 className="text-center" style={{ fontSize: "35px" }}>
                    {userRole ? "FOG" : "Rx Agad"}
                    <span style={{ display: "block" }}>
                      {userRole ? userRole : "Lab Personnel"} APP
                    </span>
                  </h2>
                </div>
                <div className="version tc_version_content">
                  <p>
                    {process.env.REACT_APP_SERVER} Version{" "}
                    {process.env.REACT_APP_VERSION}
                  </p>
                </div>
              </div>
              <Form.Group className="tc-login-form-list">
                <div className="tc-login-form-list-input-row mb-3">
                  <Form.Control
                    type="text"
                    name="username"
                    value={values.username}
                    onChange={handleInputChange}
                    placeholder="Username"
                    className="text-center"
                  />
                  <span className="tc_error_text2">
                    {errors && errors.username}
                  </span>
                </div>
              </Form.Group>
              <Form.Group className="tc-login-form-list">
                <div className="tc-login-form-list-input-row">
                  <Form.Control
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    className="text-center"
                  />
                  <span className="tc_error_text2">
                    {errors && errors.password}
                  </span>
                </div>
              </Form.Group>
              <div className="text-center">
                <Button
                  variant="primary"
                  className="login-button mt-5"
                  type="submit"
                  onClick={(e) => handleOfflineOnlineLogin(e)}
                  disabled={isLoading}
                >
                  Login
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 4.5l7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        <div className="tc-footer-blank d-flex justify-content-center"></div>
      </div>
      {offlineModal && (
        <OfflineModal show={offlineModal} onHide={handleOfflineModal} />
      )}
      {confirmationModal && (
        <ConfirmationModal
          show={confirmationModal}
          onHide={() => {
            setIsLoading(false);
            handleConfirmModal();
          }}
          title="Syncing Data"
          subTitle="Failed to sync data!"
          description="Please resync the data!"
          cancel="Cancel"
          save="Syncing"
          errorMessage={syncMessage}
          onSubmit={() => {
            setIsLoading(false);
            uploadAndFetchData(syncType);
          }}
        />
      )}
      {alreadyLoginModal && (
        <ConfirmationModal
          show={alreadyLoginModal}
          onHide={handleAlreadyLoginModal}
          title="Login"
          size="md"
          description={messages.toast.alreadyLogin}
          save="Ok"
          radius={true}
          onSubmit={handleAlreadyLoginModal}
        />
      )}
    </>
  );
};

export default LoginPage;
